import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Popper,
  ClickAwayListener,
  Fade,
  Stack,
} from '@mui/material';
import DragIndicator from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  NurturingStep,
  CadenceUnit,
  StepType,
  StaticStepHoverData,
} from 'app/pages/NurturingJourneySettingsComponent/types';
import { CADENCE_UNITS } from 'app/pages/NurturingJourneySettingsComponent/types';
import {
  STEP_TYPE_LABELS,
  CADENCE_UNIT_LABELS,
  DSL_NAME_OPTIONS,
  TEMPLATE_TYPE_OPTIONS,
} from 'app/pages/NurturingJourneySettingsComponent/data/constants';
import { SortableChipList } from 'app/pages/NurturingJourneySettingsComponent/components/SortableChipList';

const sortableIdPrefix = 'nurturing-step-';

export interface NurturingStepCardProps {
  step: NurturingStep;
  stepIndex: number;
  sortableId: string;
  onUpdate: (patch: Partial<NurturingStep>) => void;
  onDelete?: () => void;
  staticStepHoverData?: StaticStepHoverData;
  disabled?: boolean;
}

export function NurturingStepCard({
  step,
  stepIndex,
  sortableId,
  onUpdate,
  onDelete,
  staticStepHoverData,
  disabled,
}: NurturingStepCardProps) {
  const isStepZero = step.step_order === 0;
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: sortableId,
    disabled: isStepZero, // Disable dragging for step 0
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const handleCadenceValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
    const safe = Number.isNaN(v) ? 0 : Math.max(0, v);
    onUpdate({ cadence_value: safe });
  };

  const handleCadenceUnitChange = (e: any) => {
    onUpdate({ cadence_unit: e.target.value as CadenceUnit });
  };

  const handleStepTypeChange = (e: any) => {
    const newType = e.target.value as StepType;
    onUpdate({ step_type: newType });
  };

  const allowedStepTypes: StepType[] =
    step.generated_template_type === 'static'
      ? ['value_associated_with_previous']
      : ['fixed', 'alternate'];
  const isStaticStep = step.generated_template_type === 'static';
  const [stepAnchorEl, setStepAnchorEl] = useState<HTMLElement | null>(null);
  const allowedStaticTypes = staticStepHoverData?.allowedStaticTypes ?? [];
  const mappedByDynamicType = staticStepHoverData?.mappedByDynamicType ?? {};
  const mappedTypeRows = Object.entries(mappedByDynamicType);
  const hoverOpen = isStaticStep && Boolean(stepAnchorEl);
  const staticStepHoverContent = (
    <Paper
      elevation={4}
      sx={{
        p: 1.5,
        maxWidth: 500,
        minWidth: 360,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontSize: 13.5, fontWeight: 700, mb: 1.2, color: 'text.primary' }}
      >
        Allowed static template types
      </Typography>
      {mappedTypeRows.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.25,
            overflow: 'hidden',
          }}
        >
          {mappedTypeRows.map(([dynamicType, staticTypes], rowIndex) => (
            <Box
              key={dynamicType}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(140px, 160px) 1fr',
                alignItems: 'flex-start',
                gap: 1,
                px: 1,
                py: 0.85,
                borderBottom:
                  rowIndex < mappedTypeRows.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  lineHeight: 1.35,
                  color: 'text.secondary',
                  pt: 0.45,
                  letterSpacing: 0.1,
                }}
              >
                {dynamicType}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.6,
                  alignContent: 'flex-start',
                }}
              >
                {staticTypes.length > 0 ? (
                  staticTypes.map(staticType => (
                    <Chip
                      key={`${dynamicType}-${staticType}`}
                      label={staticType}
                      size="small"
                      sx={{
                        height: 22,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: 11,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No mapped values
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      ) : allowedStaticTypes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No mapped static template types available.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
          {allowedStaticTypes.map(type => (
            <Chip
              key={type}
              label={type}
              size="small"
              sx={{
                height: 22,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: 11,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          ))}
        </Box>
      )}
    </Paper>
  );

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={1}
      sx={{
        p: 2.5,
        mb: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        position: 'relative',
      }}
    >
      {/* Template type label: top left — hidden for step 0 */}
      {!isStepZero && (
        <Box sx={{ position: 'absolute', top: -12, left: 12, zIndex: 1 }}>
          <Chip
            label={step.generated_template_type === 'static' ? 'Static Template' : 'Dynamic Template'}
            size="small"
            sx={{
              bgcolor: step.generated_template_type === 'static' ? 'secondary.main' : 'primary.main',
              color: 'white',
              fontWeight: 600,
              borderRadius: 2,
            }}
          />
        </Box>
      )}
      {/* Row 1: Drag handle, Step label, Step type, Cadence (value + unit), Delete — one aligned row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        {/* Drag handle - hidden for step 0 */}
        {!isStepZero && (
          <Box
            data-drag-handle
            sx={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}
            {...listeners}
            {...attributes}
          >
            <DragIndicator fontSize="small" color="action" />
          </Box>
        )}
        <Box
          onMouseEnter={e => {
            if (isStaticStep) setStepAnchorEl(e.currentTarget as HTMLElement);
          }}
          onMouseLeave={() => {
            setStepAnchorEl(null);
          }}
          sx={{ display: 'inline-flex' }}
        >
          <Chip label={`Step ${step.step_order}`} size="small" sx={{ fontWeight: 600, borderRadius: 1 }} />
        </Box>
        <Popper
          open={hoverOpen}
          anchorEl={stepAnchorEl}
          placement="right-start"
          transition
          modifiers={[
            { name: 'offset', options: { offset: [8, 0] } },
            { name: 'preventOverflow', options: { padding: 8 } },
          ]}
          sx={{ zIndex: 1400 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={120}>
              <Box
                onMouseEnter={() => {
                  if (isStaticStep && stepAnchorEl) setStepAnchorEl(stepAnchorEl);
                }}
                onMouseLeave={() => setStepAnchorEl(null)}
              >
                <ClickAwayListener onClickAway={() => setStepAnchorEl(null)}>
                  <Box>{staticStepHoverContent}</Box>
                </ClickAwayListener>
              </Box>
            </Fade>
          )}
        </Popper>
        {/* Step type dropdown - hidden for step 0 */}
        {!isStepZero && (
          <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel>Step type</InputLabel>
            <Select
              label="Step type"
              value={step.step_type}
              onChange={handleStepTypeChange}
              disabled={disabled}
            >
              {allowedStepTypes.map(t => (
                <MenuItem key={t} value={t}>
                  {STEP_TYPE_LABELS[t] ?? t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {/* Step type display for step 0 - read-only */}
        {isStepZero && (
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            Step type: {STEP_TYPE_LABELS[step.step_type] ?? step.step_type}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderLeft: 1,
            borderColor: 'divider',
            pl: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Cadence
          </Typography>
          <TextField
            size="small"
            type="number"
            value={step.cadence_value}
            onChange={handleCadenceValueChange}
            disabled={disabled}
            inputProps={{ min: 0, step: 1, onWheel: e => (e.target as HTMLInputElement).blur() }}
            sx={{ width: 72 }}
          />
          <FormControl size="small" sx={{ minWidth: 96 }}>
            <InputLabel>Unit</InputLabel>
            <Select
              label="Unit"
              value={step.cadence_unit}
              onChange={handleCadenceUnitChange}
              disabled={disabled}
            >
              {CADENCE_UNITS.map(u => (
                <MenuItem key={u} value={u}>
                  {CADENCE_UNIT_LABELS[u] ?? u}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {/* Delete button — inline at end of row, only for non-step-0 */}
        {!isStepZero && onDelete && (
          <Tooltip title="Delete step">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={onDelete}
                disabled={disabled}
                aria-label="delete step"
                sx={{ ml: 'auto', flexShrink: 0 }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Row 2: Type-specific fields */}
      <Box>
          {step.step_type === 'fixed' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Template types</InputLabel>
              <Select
                label="Template types"
                multiple
                value={step.template_types ?? []}
                onChange={e => onUpdate({ template_types: e.target.value as string[] })}
                disabled={disabled}
                renderValue={selected => {
                  const arr = selected as string[];
                  return (
                    <Box component="span" sx={{ display: 'block', minHeight: 32 }}>
                      <SortableChipList
                        items={arr}
                        onReorder={newOrder => onUpdate({ template_types: newOrder })}
                        disabled={disabled}
                      />
                    </Box>
                  );
                }}
              >
                {TEMPLATE_TYPE_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {step.step_type === 'value_associated_with_previous' && (
            <FormControl size="small" fullWidth>
              <InputLabel>DSL name</InputLabel>
              <Select
                label="DSL name"
                value={step.dsl_name ?? ''}
                onChange={e => onUpdate({ dsl_name: e.target.value as string })}
                disabled={disabled}
              >
                {DSL_NAME_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {step.step_type === 'alternate' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Alternate options</InputLabel>
              <Select
                label="Alternate options"
                multiple
                value={step.alternate_options ?? []}
                onChange={e => onUpdate({ alternate_options: e.target.value as string[] })}
                disabled={disabled}
                renderValue={selected => {
                  const arr = selected as string[];
                  return (
                    <Box component="span" sx={{ display: 'block', minHeight: 32 }}>
                      <SortableChipList
                        items={arr}
                        onReorder={newOrder => onUpdate({ alternate_options: newOrder })}
                        disabled={disabled}
                      />
                    </Box>
                  );
                }}
              >
                {TEMPLATE_TYPE_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
      </Box>
    </Paper>
  );
}
